import { AvailableRoomPlan, RoomCancellationPolicy } from '@/__generated';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import hotelService from '@/services/hotelService';
import { faCircleInfo, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { maxBy, minBy, sortBy } from 'lodash';
import React from 'react';
import { Popover, PopoverBody, PopoverHeader } from 'reactstrap';

type Props = {
  plan: AvailableRoomPlan;
  checkinDate: any;
  fontSize?: number;
};

export default function DefaultCancellationPolicy({ plan, checkinDate, fontSize = 14 }: Props) {
  const [isOpen, setIsOpen] = React.useState(false);
  const ref = React.useRef<any>();
  const { formatCurrency } = useCurrencyFormat();

  const cancellationPolicies = React.useMemo(() => {
    if (plan && plan.cancellationPolicies?.length && checkinDate) {
      let policies = sortBy(plan.cancellationPolicies || [], x => x.dayPeriod);
      if (policies?.length) {
        if (policies.some(policy => policy.dayPeriod === 0 && policy.value === 100)) {
          return policies.filter(x => (x.dayPeriod || 0) >= 0).reverse();
        }
        return policies.reverse();
      } else {
        let policy = minBy(plan.cancellationPolicies, x => x.dayPeriod) || {};
        return [policy];
      }
    }
    return [];
  }, [plan, checkinDate]);

  let lowestPoliciesText = React.useMemo(() => {
    if (cancellationPolicies?.length && checkinDate) {
      let cancellationPolicy = maxBy(cancellationPolicies, x => x.dayPeriod);
      return hotelService.getDefaultCancellationPolicyText(cancellationPolicy, checkinDate, formatCurrency);
    }
    return undefined;
  }, [cancellationPolicies, checkinDate, formatCurrency]);

  if (plan.nonRefundable) {
    return (
      <div className="d-flex items-center text-red-1 fw-500">
        <FontAwesomeIcon icon={faTriangleExclamation} className="text-12 mr-10" />
        <div className="text-15">
          Non-refundable
          <>
            <FontAwesomeIcon
              onMouseOver={event => {
                setIsOpen(true);
              }}
              onMouseLeave={() => {
                setIsOpen(false);
              }}
              ref={ref}
              icon={faCircleInfo}
              className="text-blue-1 font-16 ml-5"
              style={{ cursor: 'pointer' }}
            />

            <Popover isOpen={isOpen} target={ref} className="room-plan-cancellation-policies" trigger="focus" placement="top">
              <PopoverHeader className="bg-blue-1 text-white">Cancellation Policies</PopoverHeader>
              <PopoverBody>
                <div className="mt-5 mb-5 pl-30">
                  <div className=" items-center text-red-1 fw-500">Non-refundable</div>
                  <p className="text-14">100% of Hotel fee</p>
                </div>
              </PopoverBody>
            </Popover>
          </>
        </div>
      </div>
    );
  }
  if (!lowestPoliciesText) {
    return null;
  }

  return (
    <div className="d-flex items-center text-green-2">
      <i className="icon-check text-12 mr-10" />
      <div className={`text-${fontSize} fw-500`}>
        {lowestPoliciesText}
        {(cancellationPolicies?.length || 0) >= 2 ? (
          <>
            <FontAwesomeIcon
              onMouseOver={event => {
                setIsOpen(true);
              }}
              onMouseLeave={() => {
                setIsOpen(false);
              }}
              ref={ref}
              icon={faCircleInfo}
              className="text-blue-1 font-16 ml-5"
              style={{ cursor: 'pointer' }}
            />

            <Popover isOpen={isOpen} target={ref} className="room-plan-cancellation-policies" trigger="focus" placement="top">
              <PopoverHeader className="bg-blue-1 text-white">Cancellation Policies</PopoverHeader>
              <PopoverBody>
                <div className="mt-5 mb-5 pl-30">
                  <ul className="timeline">
                    {cancellationPolicies?.map(cancellationPolicy => {
                      return (
                        <li key={cancellationPolicy.dayPeriod}>
                          <span className={cancellationPolicy.dayPeriod === 0 ? 'text-red-1 fw-500' : 'text-blue-1 fw-500'}>
                            {hotelService.getCancellationPolicyDate(cancellationPolicy.dayPeriod || 0, checkinDate || '')}
                          </span>
                          <p className="text-14">{hotelService.getCancellationPolicyText(cancellationPolicy, checkinDate || '', formatCurrency)}</p>
                        </li>
                      );
                    })}
                  </ul>

                  {plan.cancellationNotes ? (
                    <div className="mt-10">
                      <div className="fw-700 text-danger">*IMPORTANCE</div>
                      <span className="italic text-red-1">{plan.cancellationNotes}</span>
                    </div>
                  ) : null}
                </div>
              </PopoverBody>
            </Popover>
          </>
        ) : null}
      </div>
    </div>
  );
}
